import { useEffect, useState } from "react";
import {
  ChevronRight,
  PhoneIcon,
  WarningCircle,
} from "../../assets/icons";
import { floatToTime } from "../../utils/Time";
import Badges from "../badges";
import UseContext from "../../hooks/useContext";
import useModal from "../modal/useModal";
import Tooltip from "../tooltips/Tooltips";
import { formatHourlyTrackingWorkerName } from "../../utils/formatHourlyTrackingWorkerName";

const TableHourTrackingMission = ({
  mission,
  navigateToMission,
  isWaiting = false,
}) => {
  const { setModalData } = UseContext();
  const { toggle } = useModal();
  const [isDateBeforeDateNow, setIsDateBeforeDateNow] = useState(false);
  const [isDateAfterDateNow, setIsDateAfterDateNow] = useState(false);

  const openEditPhoneModal = (e) => {
    e.stopPropagation();
    if (!mission.user?.idTempWorker) return;
    setModalData({
      idTempWorker: mission.user.idTempWorker,
      phone: mission.user.phone || "",
      firstname: mission.user.firstname,
      lastname: mission.user.lastname,
    });
    toggle("edit_temp_worker_phone");
  };

  useEffect(() => {
    if (!isWaiting) {
      const dateNow = new Date(new Date().toISOString().split("T")[0]);
      const dateMission = new Date(
        new Date(mission.date).toISOString().split("T")[0]
      );

      //compare date year month day
      setIsDateBeforeDateNow(
        new Date(dateMission).getTime() < new Date(dateNow).getTime()
      );
      setIsDateAfterDateNow(
        new Date(dateMission).getTime() > new Date(dateNow).getTime()
      );
    }
  }, [mission, isWaiting]);

  return (
    <>
      {isWaiting ? (
        <div
          className={`grid grid-cols-5 border rounded-lg gap-8 px-4 py-3 text-sm`}
        >
          <div className={"flex flex-col justify-center"}>
            <div className={"h-4 bg-gray-200 rounded w-40"}></div>
          </div>
          <div className={"flex flex-col justify-center gap-1"}>
            <div className={"h-4 bg-gray-200 rounded w-20"}></div>
            <div className={"h-4 bg-gray-200 rounded w-40"}></div>
          </div>
          <div className={"flex flex-col justify-center gap-1"}>
            <div className={"h-4 bg-gray-200 rounded w-20"}></div>
            <div className={"h-4 bg-gray-200 rounded w-40"}></div>
          </div>
          <div className={"flex flex-col justify-center gap-1"}>
            <div className={"h-4 bg-gray-200 rounded w-20"}></div>
            <div className={"h-4 bg-gray-200 rounded w-40"}></div>
          </div>
          <div className={"flex justify-end items-center gap-2"}>
            <div className={"h-6 bg-gray-200 rounded w-20"}></div>
            <div className={"h-6 bg-gray-200 rounded w-6"}></div>
          </div>
        </div>
      ) : (
        <div
          onClick={() => navigateToMission(mission.idMission, mission.date)}
          className={
            "grid grid-cols-5 border rounded-lg cursor-pointer gap-8 bg-white px-4 py-3 text-sm transition-colors hover:bg-gray-50 active:bg-gray-100"
          }
        >
          <div className={"flex flex-col justify-center"}>
            <div className={"truncate"}>
              {formatHourlyTrackingWorkerName(
                mission.user.firstname,
                mission.user.lastname,
              )}
            </div>
          </div>
          <div className={"flex flex-col justify-center"}>
            <div>Mission</div>
            <div className={"text-gray-500 truncate"}>{mission.idMission}</div>
          </div>
          <div className={"flex flex-col justify-center"}>
            <div>Agence</div>
            <div className={"text-gray-500 truncate"}>
              {mission.interimAgency?.name?.trim() ||
                mission.interimAgency?.reference?.trim() ||
                "—"}
            </div>
          </div>
          <div className={"flex flex-col justify-center"}>
            {mission?.markup?.start && (
              <>
                <div>Émargé</div>
                <div className={"text-gray-500"}>
                  {mission?.markup?.start} {">"} {mission?.markup?.end || "-"}
                  {mission?.markup?.pause &&
                    ` (${floatToTime(mission?.markup?.pause, "'")})`}
                </div>
              </>
            )}
          </div>
          <div className={"flex justify-end items-center"}>
            <div className={"flex items-center gap-3"}>
              {!mission.user.phone && (
                <Tooltip placement="top" text="Ajouter un numéro">
                  <button
                    type="button"
                    aria-label="Ajouter un numéro"
                    onClick={openEditPhoneModal}
                    className={`flex shrink-0 items-center justify-center rounded p-0.5 ${
                      mission.user?.idTempWorker
                        ? "cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-workfully-700"
                        : "cursor-not-allowed opacity-60"
                    }`}
                    disabled={!mission.user?.idTempWorker}
                  >
                    <PhoneIcon wh={20} color={"#EF4444"} />
                  </button>
                </Tooltip>
              )}
              {mission.isFlexibilityDate && (
                <Badges color={"PRIMARY"}>Souplesse</Badges>
              )}
              <div className={"text-nowrap"}>
                {mission.markup.start &&
                  mission.markup.end &&
                  !isDateAfterDateNow && (
                    <>
                      {isDateBeforeDateNow ? (
                        <Badges color={"INFO"}>Shift terminé</Badges>
                      ) : (
                        <Badges color={"SUCCESS"}>En cours</Badges>
                      )}
                    </>
                  )}
                {mission.markup.start &&
                  !mission.markup.end &&
                  !isDateBeforeDateNow &&
                  !isDateAfterDateNow && (
                    <Badges color={"SUCCESS"}>En cours</Badges>
                  )}
                {mission.markup.start &&
                  !mission.markup.end &&
                  isDateBeforeDateNow &&
                  !isDateAfterDateNow && (
                    <Badges color={"ALERT"}>
                      <div className={"flex gap-1 items-center"}>
                        <div>
                          <WarningCircle wh={16} color={"#EF4444"} />{" "}
                        </div>
                        <div>Manquant</div>
                      </div>
                    </Badges>
                  )}
                {!mission.markup.start &&
                  !mission.markup.end &&
                  !isDateAfterDateNow && (
                    <Badges color={"SECONDARY"}>Non émargé</Badges>
                  )}
              </div>
              <ChevronRight wh={20} color={"#6B7280"} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TableHourTrackingMission;
